export type Quantity =
  | "length"
  | "mass"
  | "area"
  | "volume"
  | "density"
  | "unitWeight"
  | "stress"
  | "force"
  | "temperature";

export type UnitSystem = "SI" | "ImperialUS";

export type UnitDefinition = {
  // `id` is persisted verbatim in saved test documents — renaming one drops users' stored units.
  readonly id: string;
  readonly symbol: string;
  readonly factor: number;
  readonly offset: number;
  readonly system: UnitSystem;
};

export type QuantityDefinition = {
  readonly baseUnitId: string;
  readonly decimalPlaces: number;
  readonly units: readonly UnitDefinition[];
};

const U = (id: string, symbol: string, factor: number, system: UnitSystem, offset = 0): UnitDefinition =>
  Object.freeze({ id, symbol, factor, offset, system });

// Imperial factors are exact, derived from 1 in = 25.4 mm and 1 lb = 0.45359237 kg.
export const UNITS: Readonly<Record<Quantity, QuantityDefinition>> = {
  length: {
    baseUnitId: "mm",
    decimalPlaces: 3,
    units: [
      U("mm", "mm", 1, "SI"),
      U("um", "µm", 0.001, "SI"),
      U("cm", "cm", 10, "SI"),
      U("m", "m", 1000, "SI"),
      U("in", "in", 25.4, "ImperialUS"),
      U("ft", "ft", 304.8, "ImperialUS"),
    ],
  },
  mass: {
    baseUnitId: "g",
    decimalPlaces: 2,
    units: [
      U("g", "g", 1, "SI"),
      U("kg", "kg", 1000, "SI"),
      U("lb", "lb", 453.59237, "ImperialUS"),
      U("oz", "oz", 28.349523125, "ImperialUS"),
    ],
  },
  // Base cm², not the square of length's mm base: an area factor equals its length counterpart squared
  // only once both are normalised to cm.
  area: {
    baseUnitId: "cm2",
    decimalPlaces: 2,
    units: [
      U("cm2", "cm²", 1, "SI"),
      U("mm2", "mm²", 0.01, "SI"),
      U("m2", "m²", 1e4, "SI"),
      U("in2", "in²", 6.4516, "ImperialUS"),
    ],
  },
  volume: {
    baseUnitId: "cm3",
    decimalPlaces: 2,
    units: [
      U("cm3", "cm³", 1, "SI"),
      // The same volume as cm³, and deliberately a second id: labs write mL for a liquid and cm³ for a
      // solid, and a field carries the one its own reading is taken in.
      U("mL", "mL", 1, "SI"),
      U("mm3", "mm³", 0.001, "SI"),
      U("L", "L", 1000, "SI"),
      U("m3", "m³", 1e6, "SI"),
      U("in3", "in³", 16.387064, "ImperialUS"),
    ],
  },
  density: {
    baseUnitId: "Mgm3",
    decimalPlaces: 2,
    units: [
      U("Mgm3", "Mg/m³", 1, "SI"),
      U("gml", "g/mL", 1, "SI"),
      U("pcf", "pcf", 0.016018463373960137, "ImperialUS"),
    ],
  },
  unitWeight: {
    baseUnitId: "kNm3",
    decimalPlaces: 2,
    units: [U("kNm3", "kN/m³", 1, "SI"), U("lbfft3", "lbf/ft³", 0.15708746384624617, "ImperialUS")],
  },
  stress: {
    baseUnitId: "kPa",
    decimalPlaces: 1,
    units: [U("kPa", "kPa", 1, "SI"), U("MPa", "MPa", 1000, "SI"), U("psi", "psi", 6.894757293168361, "ImperialUS")],
  },
  // No ASTM-reachable field yet (every [N] label in the app is dss/NEN); kept per the requirements table.
  force: {
    baseUnitId: "N",
    decimalPlaces: 2,
    units: [U("N", "N", 1, "SI"), U("kN", "kN", 1000, "SI"), U("lbf", "lbf", 4.4482216152605, "ImperialUS")],
  },
  temperature: {
    baseUnitId: "C",
    decimalPlaces: 1,
    units: [U("C", "°C", 1, "SI"), U("F", "°F", 5 / 9, "ImperialUS", -160 / 9)],
  },
};

for (const definition of Object.values(UNITS)) {
  Object.freeze(definition.units);
  Object.freeze(definition);
}
Object.freeze(UNITS);

export const unitsOf = (quantity: Quantity): readonly UnitDefinition[] => UNITS[quantity].units;

// For the one caller that must not throw: a unit id read back from a saved document, where an id
// retired since the document was written would otherwise take a form down mid-render.
export const tryFindUnit = (quantity: Quantity, id: string): UnitDefinition | undefined =>
  UNITS[quantity].units.find((candidate) => candidate.id === id);

export const findUnit = (quantity: Quantity, id: string): UnitDefinition => {
  const unit = tryFindUnit(quantity, id);
  if (!unit) throw new Error(`Unknown unit "${id}" for quantity "${quantity}"`);
  return unit;
};
